import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import OutlinedInput from '@mui/material/OutlinedInput'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import brushfireLogo from '@/features/auth/assets/login/brushfire-logo.png'
import { AuthCard, OtpInput } from '@/features/auth/components'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { ApiError } from '@/api/http/types'
import {
  usePublicDeletionResend,
  usePublicDeletionStart,
  usePublicDeletionVerify,
} from '../api/usePublicDeletion'
import { TurnstileWidget } from '../components/TurnstileWidget'
import {
  DEFAULT_GRACE_DAYS,
  DELETE_ACCOUNT_EMAIL_STATE_KEY,
  readDeleteAccountEmailFromState,
} from '../lib/constants'
import { DELETION_COPY, RETENTION_DISCLOSURE } from '../lib/deletion-disclosure'
import {
  extractPublicDeletionMaskedEmail,
  isPublicDeletionOtpIssued,
} from '../lib/public-deletion-response'
import { palettePrimaryMain } from '@/theme/designTokens'

const emailSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
})

type EmailFormData = z.infer<typeof emailSchema>

const RESEND_COOLDOWN_SECONDS = 120

type Step = 'intro' | 'otp' | 'success'

type DeletionDisclosureProps = Readonly<{
  graceDays: number
}>

function DeletionDisclosure({ graceDays }: DeletionDisclosureProps) {
  return (
    <Box component="section" sx={{ textAlign: 'left', width: '100%', mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {DELETION_COPY.intro}
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {DELETION_COPY.deletedHeading}
      </Typography>
      <Typography component="ul" variant="body2" color="text.secondary" sx={{ pl: 2, mb: 1.5 }}>
        {DELETION_COPY.deletedItems.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {DELETION_COPY.retainedHeading}
      </Typography>
      <Typography component="ul" variant="body2" color="text.secondary" sx={{ pl: 2, mb: 1.5 }}>
        <li>
          Reward and payout transaction records (tax, fraud prevention, financial audit) —
          retained up to {RETENTION_DISCLOSURE.rewardPayoutRecordsYears} years; PII severed via
          anonymized identifiers
        </li>
        <li>
          Identity verification outcomes required for compliance — up to{' '}
          {RETENTION_DISCLOSURE.kycFraudOutcomesYears} years
        </li>
        <li>
          Append-only audit events with anonymized user tokens — up to{' '}
          {RETENTION_DISCLOSURE.auditLogYears} years
        </li>
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {DELETION_COPY.balanceHeading}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {DELETION_COPY.balanceBody}
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {DELETION_COPY.timelineHeading}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        After email verification, deletion is scheduled with a {graceDays}-day grace period. You may
        cancel by signing in to the BrushFire app within that period. {DELETION_COPY.irreversibilityNote}{' '}
        You will receive email confirmation when processing completes.
      </Typography>
    </Box>
  )
}

export function DeleteAccountPage() {
  useDocumentTitle('Delete Account')
  const navigate = useNavigate()
  const location = useLocation()
  const emailFromRoute = readDeleteAccountEmailFromState(location.state)

  const [step, setStep] = useState<Step>(emailFromRoute ? 'otp' : 'intro')
  const [email, setEmail] = useState(emailFromRoute ?? '')
  const [code, setCode] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [successGraceDays, setSuccessGraceDays] = useState(DEFAULT_GRACE_DAYS)
  const [scheduledPurgeAt, setScheduledPurgeAt] = useState<string | null>(null)
  const [resendCooldownUntilMs, setResendCooldownUntilMs] = useState<number | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)

  const startMutation = usePublicDeletionStart()
  const resendMutation = usePublicDeletionResend()
  const verifyMutation = usePublicDeletionVerify()

  const form = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: emailFromRoute ?? '' },
    mode: 'onChange',
  })

  useEffect(() => {
    if (!emailFromRoute && step === 'otp') {
      setStep('intro')
    }
  }, [emailFromRoute, step])

  const resendRemaining =
    resendCooldownUntilMs && resendCooldownUntilMs > Date.now()
      ? Math.ceil((resendCooldownUntilMs - Date.now()) / 1000)
      : 0

  const onSubmitEmail = (data: EmailFormData) => {
    setServerError(null)
    startMutation.mutate(
      { email: data.email, turnstileToken: turnstileToken ?? undefined },
      {
        onSuccess: (result) => {
          const maskedEmail = extractPublicDeletionMaskedEmail(result)
          if (!isPublicDeletionOtpIssued(maskedEmail)) {
            setServerError(DELETION_COPY.emailNotSent)
            return
          }
          setEmail(data.email)
          navigate('.', {
            replace: true,
            state: { [DELETE_ACCOUNT_EMAIL_STATE_KEY]: data.email },
          })
          setStep('otp')
          setResendCooldownUntilMs(Date.now() + RESEND_COOLDOWN_SECONDS * 1000)
        },
        onError: (err) => {
          const message =
            err instanceof ApiError ? err.message : 'Something went wrong. Please try again.'
          setServerError(message)
        },
      }
    )
  }

  const handleVerify = () => {
    if (!confirmed) {
      setServerError('Please confirm you understand this action is permanent.')
      return
    }
    setServerError(null)
    verifyMutation.mutate(
      { email, code, confirm: true },
      {
        onSuccess: (result) => {
          const grace = result.graceDays ?? result.deletion?.graceDays ?? DEFAULT_GRACE_DAYS
          setSuccessGraceDays(grace)
          setScheduledPurgeAt(
            result.scheduledPurgeAt ?? result.deletion?.scheduledPurgeAt ?? null
          )
          setStep('success')
        },
        onError: (err) => {
          const message =
            err instanceof ApiError ? err.message : 'Something went wrong. Please try again.'
          setServerError(message)
        },
      }
    )
  }

  const handleResend = () => {
    if (resendRemaining > 0) return
    setServerError(null)
    resendMutation.mutate(
      { email },
      {
        onSuccess: (result) => {
          const maskedEmail = extractPublicDeletionMaskedEmail(result)
          if (!isPublicDeletionOtpIssued(maskedEmail)) {
            setServerError(DELETION_COPY.emailNotSent)
            return
          }
          setResendCooldownUntilMs(Date.now() + RESEND_COOLDOWN_SECONDS * 1000)
        },
        onError: (err) => {
          const message =
            err instanceof ApiError ? err.message : 'Something went wrong. Please try again.'
          setServerError(message)
        },
      }
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: 2,
        py: 4,
      }}
    >
      <AuthCard maxWidth={560}>
        <Box sx={{ mb: 2 }}>
          <img src={brushfireLogo} alt="Brushfire" width={140} height={40} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
          {DELETION_COPY.pageTitle}
        </Typography>

        {step === 'intro' && (
          <>
            <DeletionDisclosure graceDays={DEFAULT_GRACE_DAYS} />
            <Divider sx={{ width: '100%', mb: 2 }} />
            <Box
              component="form"
              onSubmit={form.handleSubmit(onSubmitEmail)}
              sx={{ width: '100%' }}
            >
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Account email
              </Typography>
              <OutlinedInput
                fullWidth
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                {...form.register('email')}
                error={!!form.formState.errors.email}
                sx={{ mb: 1 }}
              />
              {form.formState.errors.email && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                  {form.formState.errors.email.message}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {DELETION_COPY.emailHint}
              </Typography>
              <TurnstileWidget onToken={setTurnstileToken} />
              {serverError && (
                <Alert severity="error" sx={{ mb: 2 }} data-testid="delete-account-email-error">
                  {serverError}
                </Alert>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={startMutation.isPending}
                sx={{ py: 1.25, bgcolor: palettePrimaryMain }}
              >
                {startMutation.isPending ? <CircularProgress size={22} color="inherit" /> : 'Send verification code'}
              </Button>
            </Box>
          </>
        )}

        {step === 'otp' && email && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
              Enter the 6-digit code sent to your email. If you did not receive it, check spam or
              request a new code.
            </Typography>
            <OtpInput value={code} onChange={setCode} disabled={verifyMutation.isPending} />
            <FormControlLabel
              sx={{ mt: 2, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  {DELETION_COPY.confirmCheckbox(DEFAULT_GRACE_DAYS)}
                </Typography>
              }
            />
            {serverError && (
              <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                {serverError}
              </Alert>
            )}
            <Button
              fullWidth
              variant="contained"
              onClick={handleVerify}
              disabled={verifyMutation.isPending || code.length < 6}
              sx={{ mt: 2, py: 1.25, bgcolor: palettePrimaryMain }}
            >
              {verifyMutation.isPending ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                'Confirm deletion request'
              )}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={handleResend}
              disabled={resendMutation.isPending || resendRemaining > 0}
              sx={{ mt: 1 }}
            >
              {resendRemaining > 0
                ? `Resend code in ${resendRemaining}s`
                : 'Resend verification code'}
            </Button>
            <Button fullWidth variant="text" onClick={() => setStep('intro')} sx={{ mt: 0.5 }}>
              Use a different email
            </Button>
          </>
        )}

        {step === 'success' && (
          <Box sx={{ textAlign: 'center' }}>
            <Alert severity="success" sx={{ mb: 2, textAlign: 'left' }}>
              {DELETION_COPY.successScheduled(successGraceDays)}
            </Alert>
            {scheduledPurgeAt && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Scheduled completion: {new Date(scheduledPurgeAt).toLocaleString()}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {DELETION_COPY.successFooter}
            </Typography>
          </Box>
        )}
      </AuthCard>
    </Box>
  )
}
